import { useState } from 'react';
import { Row, Col, Card, Empty, Select, Radio } from 'antd';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

export const RepartitionChart = ({ data = [], compagniesBeneficiaires = [] }) => {
    const [chartType, setChartType] = useState('pie');
    const [viewMode, setViewMode] = useState('beneficiaire'); // 'beneficiaire' ou 'categorie'

    // Préparer les données pour les graphiques par bénéficiaire (SAP réparti uniquement)
    const dataParBeneficiaire = compagniesBeneficiaires.map((comp, index) => {
        const totalSapReparti = data
            .filter(d => d.beneficiaireCode === comp.code)
            .reduce((sum, d) => sum + (d.montantSapReparti || 0), 0);

        return {
            name: comp.code,
            fullName: comp.nom,
            sapReparti: totalSapReparti,
            taux: comp.taux || 0,
            color: COLORS[index % COLORS.length]
        };
    }).filter(d => d.sapReparti !== 0);

    // Préparer les données par catégorie
    const categoriesSet = new Set(data.map(d => d.categorieCode));
    const dataParCategorie = Array.from(categoriesSet).map((cat, index) => {
        const items = data.filter(d => d.categorieCode === cat);
        const totalSapReparti = items.reduce((sum, d) => sum + (d.montantSapReparti || 0), 0);

        return {
            name: cat,
            fullName: items[0]?.categorieLibelle || cat,
            sapReparti: totalSapReparti,
            color: COLORS[index % COLORS.length]
        };
    }).filter(d => d.sapReparti !== 0);

    const chartData = viewMode === 'beneficiaire' ? dataParBeneficiaire : dataParCategorie;

    // Données pour le pie chart
    const pieData = chartData.map(d => ({
        name: d.name,
        value: Math.abs(d.sapReparti),
        fullName: d.fullName
    }));

    // Total SAP réparti
    const totalSapReparti = chartData.reduce((sum, d) => sum + d.sapReparti, 0);

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const percentage = totalSapReparti > 0
                ? ((payload[0]?.value || 0) / totalSapReparti * 100).toFixed(1)
                : 0;
            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, marginBottom: 8 }}>
                        {payload[0]?.payload?.fullName || label}
                    </p>
                    <p style={{ margin: '4px 0', color: '#ff4d4f', fontSize: 14, fontWeight: 600 }}>
                        SAP: {displayNumbers(payload[0]?.value || 0)} F
                    </p>
                    <p style={{ margin: '4px 0', color: '#888', fontSize: 12 }}>
                        Part: {percentage}%
                    </p>
                </div>
            );
        }
        return null;
    };

    // Custom label pour pie chart
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        if (percent < 0.05) return null;

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={500}
            >
                {`${name} (${(percent * 100).toFixed(0)}%)`}
            </text>
        );
    };

    if (data.length === 0 || chartData.length === 0) {
        return (
            <Empty
                description="Aucune donnée SAP disponible pour les graphiques"
                style={{ padding: '60px 0' }}
            />
        );
    }

    return (
        <div>
            {/* Contrôles */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col>
                    <span style={{ marginRight: 8, fontWeight: 500 }}>Vue:</span>
                    <Radio.Group
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="beneficiaire">Par bénéficiaire</Radio.Button>
                        <Radio.Button value="categorie">Par catégorie</Radio.Button>
                    </Radio.Group>
                </Col>
                <Col>
                    <span style={{ marginRight: 8, fontWeight: 500 }}>Type:</span>
                    <Select
                        value={chartType}
                        onChange={setChartType}
                        style={{ width: 150 }}
                        options={[
                            { value: 'pie', label: '🥧 Camembert' },
                            { value: 'bar', label: '📊 Barres verticales' },
                            { value: 'barHorizontal', label: '📊 Barres horizontales' },
                            { value: 'area', label: '📈 Aires' }
                        ]}
                    />
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Graphique principal */}
                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <span>
                                SAP réparti {viewMode === 'beneficiaire' ? 'par compagnie' : 'par catégorie'}
                                <span style={{
                                    marginLeft: 12,
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: '#ff4d4f'
                                }}>
                                    Total: {displayNumbers(totalSapReparti)} F
                                </span>
                            </span>
                        }
                        style={{ height: '100%' }}
                    >
                        <ResponsiveContainer width="100%" height={400}>
                            {chartType === 'pie' ? (
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                        outerRadius={150}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                </PieChart>
                            ) : chartType === 'bar' ? (
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar
                                        dataKey="sapReparti"
                                        name="SAP réparti"
                                        fill="#ff4d4f"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            ) : chartType === 'barHorizontal' ? (
                                <BarChart
                                    data={chartData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="fullName"
                                        width={100}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar
                                        dataKey="sapReparti"
                                        name="SAP réparti"
                                        fill="#ff4d4f"
                                        radius={[0, 4, 4, 0]}
                                    />
                                </BarChart>
                            ) : (
                                <AreaChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Area
                                        type="monotone"
                                        dataKey="sapReparti"
                                        name="SAP réparti"
                                        stroke="#ff4d4f"
                                        fill="#ff4d4f"
                                        fillOpacity={0.6}
                                    />
                                </AreaChart>
                            )}
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* Légende détaillée */}
                <Col xs={24} lg={8}>
                    <Card title="Détail du SAP réparti" style={{ height: '100%' }}>
                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                            {chartData
                                .sort((a, b) => b.sapReparti - a.sapReparti)
                                .map((item, index) => {
                                    const percentage = totalSapReparti > 0
                                        ? (item.sapReparti / totalSapReparti * 100)
                                        : 0;
                                    return (
                                        <div
                                            key={item.name}
                                            style={{
                                                padding: '12px',
                                                marginBottom: 8,
                                                background: '#fafafa',
                                                borderRadius: 8,
                                                borderLeft: `4px solid ${COLORS[index % COLORS.length]}`
                                            }}
                                        >
                                            <div style={{
                                                fontWeight: 600,
                                                marginBottom: 8,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <span>{item.fullName}</span>
                                                <span style={{
                                                    fontSize: 11,
                                                    color: '#888',
                                                    background: '#fff',
                                                    padding: '2px 8px',
                                                    borderRadius: 4
                                                }}>
                                                    {item.name}
                                                </span>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: 8
                                            }}>
                                                <div style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 16 }}>
                                                    {displayNumbers(item.sapReparti)} F
                                                </div>
                                                <div style={{
                                                    fontSize: 12,
                                                    color: '#722ed1',
                                                    fontWeight: 600
                                                }}>
                                                    {percentage.toFixed(1)}%
                                                </div>
                                            </div>

                                            {/* Barre de progression */}
                                            <div style={{
                                                height: 6,
                                                background: '#e8e8e8',
                                                borderRadius: 3,
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${percentage}%`,
                                                    height: '100%',
                                                    background: COLORS[index % COLORS.length],
                                                    borderRadius: 3,
                                                    transition: 'width 0.3s ease'
                                                }} />
                                            </div>

                                            {item.taux > 0 && (
                                                <div style={{
                                                    marginTop: 8,
                                                    fontSize: 11,
                                                    color: '#1890ff'
                                                }}>
                                                    Taux de répartition: {item.taux.toFixed(2)}%
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>

                        {/* Total SAP */}
                        <div style={{
                            marginTop: 16,
                            padding: 16,
                            background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
                            borderRadius: 8,
                            color: 'white'
                        }}>
                            <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>
                                TOTAL SAP RÉPARTI
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 700 }}>
                                {displayNumbers(totalSapReparti)} F
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                                {chartData.length} {viewMode === 'beneficiaire' ? 'compagnie(s)' : 'catégorie(s)'}
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Graphique comparatif par bénéficiaire/catégorie */}
            <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card title={`Classement SAP ${viewMode === 'beneficiaire' ? 'par compagnie' : 'par catégorie'}`}>
                        <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 50)}>
                            <BarChart
                                data={chartData.sort((a, b) => b.sapReparti - a.sapReparti)}
                                layout="vertical"
                                margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="fullName"
                                    width={120}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar
                                    dataKey="sapReparti"
                                    name="SAP réparti"
                                    fill="#ff4d4f"
                                    radius={[0, 4, 4, 0]}
                                    label={{
                                        position: 'right',
                                        formatter: (value) => `${displayNumbers(value)} F`,
                                        fontSize: 11,
                                        fill: '#666'
                                    }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};